/**
 * What a finished simulation amounts to, derived from what the engine already
 * told us.
 *
 * Nothing here reads the database or the opaque session: the closure's details
 * are in the tool calls the model made, and the documents it would have
 * written are in the same event feed the technical panel renders. So a recap
 * costs one pass over `events` and no round trip.
 *
 * THE RULE THAT MATTERS: read the tool's RESULT, not its parameters. The
 * parameters are what the model asked for; the result is what the engine
 * granted, and the two differ in cases a colleague will actually hit. A
 * `close_positive` returns `ok: true` even when the closure gate only
 * PROPOSED the offer to a person — `aiClosureAllowed !== true`, a deliberate
 * setting for offers nobody wants an agent closing alone — and that is a
 * handoff, not a sale. Sourcing the badge from the parameters would show
 * "closed on Offer A" beside "handed over to the call center" on the same
 * screen, claiming a sale that did not happen, in the one tool built to stop
 * people trusting a configuration they should not.
 */

const lastOkCall = (events, name) =>
  events
    .filter(
      (event) => event.type === 'toolCall' && event.name === name && event.ok,
    )
    .pop() || null;

const params = (call) => (call && call.params) || {};
const result = (call) => ((call && call.result) || {}).data || {};

const writesTo = (events, prefix) =>
  events.filter(
    (event) =>
      event.type === 'write' && String(event.path || '').startsWith(prefix),
  );

/**
 * Offers by name. The engine deals in ids, and an id tells a colleague
 * nothing. An id we cannot resolve is kept and shown as itself rather than
 * dropped: a closure on an order the page has not loaded is a finding, not a
 * row to hide.
 */
const resolveOrders = (ids, monetizationOrders) =>
  (Array.isArray(ids) ? ids : []).map((id) => {
    const order = (monetizationOrders || []).find(
      (candidate) => candidate.id === id,
    );
    return { id, name: (order && order.name) || id, resolved: !!order };
  });

/**
 * Whether the causale the model chose would actually have been recorded.
 *
 * The engine keeps it only when a single offer closes AND that offer carries
 * a list of reasons — otherwise it is dropped silently. Saying which of the
 * two conditions failed is the difference between "the agent forgot" and
 * "nobody configured a list".
 */
const reasonFate = (closedIds, monetizationOrders) => {
  if (closedIds.length !== 1) return 'severalOffers';
  const order = (monetizationOrders || []).find(
    (candidate) => candidate.id === closedIds[0],
  );
  const reasons = order && Array.isArray(order.reasons) ? order.reasons : [];
  return reasons.length ? 'recorded' : 'noListOnTheOffer';
};

export const buildRecap = ({
  status,
  cost,
  messages,
  events,
  monetizationOrders,
}) => {
  const feed = Array.isArray(events) ? events : [];
  const transcript = Array.isArray(messages) ? messages : [];

  const positiveCall = lastOkCall(feed, 'close_positive');
  const negativeCall = lastOkCall(feed, 'close_negative');
  const appointmentCall = lastOkCall(feed, 'book_appointment');

  // One ok:true close_positive, two outcomes. `data.closed` is present only
  // on a real closure; `data.proposed` only on an escalation.
  const closedIds = Array.isArray(result(positiveCall).closed)
    ? result(positiveCall).closed
    : null;
  const proposed = result(positiveCall).proposed === true;

  return {
    outcome: (status && status.outcome) || null,
    handoffReason: (status && status.handoffReason) || null,
    costUsd: (cost && cost.sessionUsd) || 0,
    interactions: {
      lead: transcript.filter((message) => message.role === 'user').length,
      agent: transcript.filter((message) => message.role === 'assistant')
        .length,
      total: transcript.length,
    },

    positive: closedIds
      ? {
          orders: resolveOrders(closedIds, monetizationOrders),
          // Offers the model asked to close that the gate refused while
          // others went through: a configuration finding, and invisible
          // anywhere else in the recap.
          notClosed: resolveOrders(
            result(positiveCall).notClosed,
            monetizationOrders,
          ),
          reason: params(positiveCall).reason || '',
          reasonFate: reasonFate(closedIds, monetizationOrders),
          message: result(positiveCall).message || '',
        }
      : null,

    // Not a sale. The offer needs a person to confirm it, so the conversation
    // went to the call center with the interest recorded.
    proposal: proposed
      ? {
          orders: resolveOrders(
            params(positiveCall).monetizationOrderIds,
            monetizationOrders,
          ),
          message: result(positiveCall).message || '',
        }
      : null,

    appointment: appointmentCall
      ? {
          date: params(appointmentCall).date || '',
          time: params(appointmentCall).time || '',
          reason: params(appointmentCall).reason || '',
          // The new call is born with userId and appointmentUserId empty:
          // nobody at the call center is assigned, whoever picks it up
          // handles it. Said explicitly because "an appointment" invites the
          // assumption that it has an owner.
          unassigned: true,
          callsTouched: writesTo(feed, 'calls/').length,
        }
      : null,

    // Calls the engine refused to run because the turn had already ended at an
    // earlier outcome in the same batch. Top level and not inside `positive`
    // on purpose: a dropped close_positive can sit beside a real closure, a
    // proposal or an appointment, and it is a finding in all of them.
    //
    // THE ONE EXCEPTION to the rule at the top of this file — read the
    // result, not the parameters — and it holds for the reason the rule
    // exists. There is no result: the call never ran. The parameters are the
    // whole story, because they are what the lead accepted and what the
    // engine never granted.
    discarded: feed
      .filter((event) => event.type === 'discardedToolCall')
      .map((event) => ({
        name: event.name,
        orders: resolveOrders(
          (event.params || {}).monetizationOrderIds,
          monetizationOrders,
        ),
        reason: (event.params || {}).reason || '',
      })),

    negative: negativeCall
      ? {
          reason: params(negativeCall).reason || '',
          // The sentence the lead actually received: the engine substitutes
          // its own when the model omits one, so the parameter would show
          // nothing where something was in fact sent.
          message: result(negativeCall).message || '',
        }
      : null,
  };
};

export default buildRecap;
